import React, { useEffect, useMemo, useRef, useState } from "react";

import { API_BASE_URL } from "../config/apiBase";
import { getVerificationOrigin } from "../config/verificationOrigin";
import { buildSubmissionRecoveryKey, getSubmissionRecoveryKeys } from "./feedbackUiVersion";
import { addDownloadCacheBuster } from "../utils/certificateDownloadUrl";
import { fetchTargetIdentity, normalizeStudentId } from "../utils/targetIdentity";
import { getAuthUserHeader } from "../utils/userRoles";

const API = API_BASE_URL;

function toInt(v, fallback) {
  const n = parseInt(String(v ?? ""), 10);
  return Number.isNaN(n) ? fallback : n;
}

async function postJson(path, body) {
  const authUser = getAuthUserHeader();
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-User": authUser,
    },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => null);
  if (!res.ok || data?.ok === false) {
    throw new Error((data && (data.detail || data.error)) || "Request failed");
  }
  return data;
}

function clearDemoOnboardingCompletion(studentId) {
  const sid = String(studentId || "").trim();
  if (!sid) return;
  const upper = sid.toUpperCase();
  try {
    localStorage.removeItem(`writeflow_demo_onboarding_completed_${upper}`);
    if (upper !== sid) {
      localStorage.removeItem(`writeflow_demo_onboarding_completed_${sid}`);
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
}

function clearStudentDraftCache(studentId) {
  const sid = String(studentId || "").trim();
  if (!sid) return;
  const upper = sid.toUpperCase();
  const prefixes = [`draft:${upper}:`, `draft:${sid}:`];
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (prefixes.some((prefix) => key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
}

function clearStudentRuntimeCache(studentId) {
  const sid = String(studentId || "").trim();
  if (!sid) return;
  const upper = sid.toUpperCase();
  const keyPrefixes = [
    `draft:${upper}:`,
    `draft:${sid}:`,
    `structured_draft:${upper}:`,
    `structured_draft:${sid}:`,
    `pending_submission:${upper}:`,
    `pending_submission:${sid}:`,
    `writeflow_focus_mode_${upper}`,
    `writeflow_focus_mode_${sid}`,
    `writeflow_demo_onboarding_completed_${upper}`,
    `writeflow_demo_onboarding_completed_${sid}`,
  ];
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (keyPrefixes.some((prefix) => key === prefix || key.startsWith(prefix))) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
  try {
    const sessionKeys = Object.keys(sessionStorage);
    for (const key of sessionKeys) {
      if (key === `writeflow_focus_mode_${upper}` || key === `writeflow_focus_mode_${sid}`) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if sessionStorage is unavailable
  }
}

function clearStudentFeedbackCache(studentId, week, lesson) {
  const sid = String(studentId || "").trim();
  const weekNum = toInt(week, null);
  const lessonNum = toInt(lesson, null);
  if (!sid || !weekNum || !lessonNum) return;
  const lessonTokens = [
    `W${weekNum}:L${lessonNum}`,
    `w${weekNum}:l${lessonNum}`,
  ];
  const candidateKeys = [
    ...getSubmissionRecoveryKeys(sid, weekNum, lessonNum),
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:feedback_version`,
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:error`,
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:result`,
    `draft:${sid}:W${weekNum}:L${lessonNum}`,
    `structured_draft:${sid}:W${weekNum}:L${lessonNum}`,
    `lesson_result:${sid}:W${weekNum}:L${lessonNum}`,
    `result_cache:${sid}:W${weekNum}:L${lessonNum}`,
    `feedback_cache:${sid}:W${weekNum}:L${lessonNum}`,
    `latestSubmissionId:${sid}:W${weekNum}:L${lessonNum}`,
    `latestSubmission:${sid}:W${weekNum}:L${lessonNum}`,
    `latest_submission_id:${sid}:W${weekNum}:L${lessonNum}`,
    `lastSubmissionId:${sid}:W${weekNum}:L${lessonNum}`,
    `lastSubmission:${sid}:W${weekNum}:L${lessonNum}`,
  ];
  try {
    const storageKeys = Object.keys(localStorage);
    for (const key of storageKeys) {
      if (
        candidateKeys.includes(key) ||
        lessonTokens.some((token) => key.includes(token) && key.includes(sid))
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }
  try {
    const sessionKeys = Object.keys(sessionStorage);
    for (const key of sessionKeys) {
      if (lessonTokens.some((token) => key.includes(token) && key.includes(sid))) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if sessionStorage is unavailable
  }
}

function clearLessonScopedBrowserCache(studentId, week, lesson) {
  const sid = String(studentId || "").trim();
  const weekNum = toInt(week, null);
  const lessonNum = toInt(lesson, null);
  if (!sid || !weekNum || !lessonNum) return;

  const upper = sid.toUpperCase();
  const draftKeys = [
    `draft:${upper}:W${weekNum}:L${lessonNum}`,
    `draft:${sid}:W${weekNum}:L${lessonNum}`,
    `structured_draft:${upper}:W${weekNum}:L${lessonNum}`,
    `structured_draft:${sid}:W${weekNum}:L${lessonNum}`,
  ];
  const submissionKeys = [
    ...getSubmissionRecoveryKeys(sid, weekNum, lessonNum),
    `${buildSubmissionRecoveryKey(sid, weekNum, lessonNum)}:feedback_version`,
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:error`,
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:result`,
    `pending_submission:${sid}:W${weekNum}:L${lessonNum}:feedback_version`,
    `lesson_result:${sid}:W${weekNum}:L${lessonNum}`,
    `result_cache:${sid}:W${weekNum}:L${lessonNum}`,
    `feedback_cache:${sid}:W${weekNum}:L${lessonNum}`,
    `latestSubmissionId:${sid}:W${weekNum}:L${lessonNum}`,
    `latestSubmission:${sid}:W${weekNum}:L${lessonNum}`,
    `latest_submission_id:${sid}:W${weekNum}:L${lessonNum}`,
    `lastSubmissionId:${sid}:W${weekNum}:L${lessonNum}`,
    `lastSubmission:${sid}:W${weekNum}:L${lessonNum}`,
  ];
  const lessonToken = `W${weekNum}:L${lessonNum}`;

  try {
    const storageKeys = Object.keys(localStorage);
    for (const key of storageKeys) {
      if (
        draftKeys.includes(key) ||
        submissionKeys.includes(key) ||
        (key.includes(lessonToken) && key.includes(sid))
      ) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if localStorage is unavailable
  }

  try {
    const sessionKeys = Object.keys(sessionStorage);
    for (const key of sessionKeys) {
      if (
        draftKeys.includes(key) ||
        submissionKeys.includes(key) ||
        (key.includes(lessonToken) && key.includes(sid))
      ) {
        sessionStorage.removeItem(key);
      }
    }
  } catch {
    // non-blocking if sessionStorage is unavailable
  }
}

function resetW4L4ChartIntroState() {
  try {
    localStorage.removeItem("writeflow_w4l4_chart_intro_seen");
  } catch {
    // non-blocking if localStorage is unavailable
  }
  try {
    window.dispatchEvent(new CustomEvent("writeflow:w4l4-chart-intro-reset"));
  } catch {
    // non-blocking if CustomEvent is unavailable
  }
}

export default function DevStudentResetPanel({
  defaultStudentId,
  onNavigateLesson,
  refreshSignal = 0,
}) {
  const initialSid = useMemo(
    () => String(defaultStudentId || "").trim().toUpperCase(),
    [defaultStudentId]
  );

  const [targetStudentId, setTargetStudentId] = useState(initialSid);
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [certificate, setCertificate] = useState(null);
  const [certificateWorking, setCertificateWorking] = useState(false);
  const [mentorState, setMentorState] = useState(null);
  const stateRequestRef = useRef(0);
  const targetIdRef = useRef("");
  const targetLookupRequestRef = useRef(0);
  const targetLookupPromiseRef = useRef(null);

  const [targetWeek, setTargetWeek] = useState(1);
  const [mentorWeek, setMentorWeek] = useState(1);
  const [targetLesson, setTargetLesson] = useState(1);
  const [resolvedTarget, setResolvedTarget] = useState(null);
  const [targetLookupStatus, setTargetLookupStatus] = useState("idle");

  const currentTargetId = String(targetStudentId || "").trim().toUpperCase();
  targetIdRef.current = currentTargetId;

  const getTargetId = () => String(targetStudentId || "").trim();

  const getTargetLabel = (sid, targetIdentity = resolvedTarget) => {
    const normalizedSid = normalizeStudentId(sid);
    const name =
      targetIdentity?.studentId === normalizedSid ? String(targetIdentity.name || "").trim() : "";
    return name ? `${name} (Target ID ${normalizedSid})` : `Target ID ${normalizedSid}`;
  };

  const getResetConfirmMessage = (label, sid, targetIdentity) => {
    const targetLabel = getTargetLabel(sid, targetIdentity);
    const messages = {
      "Full Course Reset": `Reset ALL COURSE PROGRESS for ${targetLabel}? This cannot be undone.`,
      "Reset Current Week": `Reset CURRENT WEEK for ${targetLabel}? This cannot be undone.`,
      "Reset Current Lesson": `Reset CURRENT LESSON for ${targetLabel}? This cannot be undone.`,
      "Reset Lesson Attempts & Feedback": "This will delete all attempts and associated feedback for the selected student, week, and lesson. Other lessons and course progress will not be affected.",
      "Reset Lesson Feedback": `Reset LESSON FEEDBACK ONLY for ${targetLabel}?`,
      "Reset Student LLM Calls": `Reset STUDENT LLM CALLS for ${targetLabel}?`,
      "Reset Week Intro": `Reset WEEK INTRO ONLY for ${targetLabel}?`,
      "Reset Weekly Reflection": `Reset WEEKLY REFLECTION ONLY for ${targetLabel}?`,
      "Reset Lesson + Reflection + Mentor Flow": `Reset ${targetLabel}, Week ${targetWeek}, Lesson ${targetLesson}? This clears only that lesson, its weekly reflection, and Mentor flow; earlier lessons, later weeks, account, certificates, and other students are preserved.`,
      "Reset Mentor Acknowledgement": `Reset Mentor acknowledgement for ${targetLabel}?`,
      "Reopen Mentor Step": `Reopen the Mentor step for ${targetLabel}?`,
    };
    return messages[label] || `Reset ${label} for ${targetLabel}?`;
  };

  const requireTargetId = () => {
    const sid = getTargetId();
    if (sid) return sid;
    const errorMessage = "Enter a Target ID before resetting.";
    setError(errorMessage);
    try {
      window.alert(errorMessage);
    } catch {
      // non-blocking if alert is unavailable
    }
    return null;
  };

  const lookupTarget = (sidArg) => {
    const requestedId = normalizeStudentId(sidArg);
    const requestToken = ++targetLookupRequestRef.current;
    setResolvedTarget(null);
    setTargetLookupStatus(requestedId ? "loading" : "idle");
    if (!requestedId) return Promise.resolve(null);
    const request = fetchTargetIdentity(requestedId)
      .then((identity) => {
        if (requestToken !== targetLookupRequestRef.current || targetIdRef.current !== requestedId) return null;
        setResolvedTarget(identity);
        setTargetLookupStatus("ready");
        return identity;
      })
      .catch((error) => {
        if (requestToken !== targetLookupRequestRef.current || targetIdRef.current !== requestedId) return null;
        setResolvedTarget(null);
        setTargetLookupStatus("error");
        setError(error.message || "Target student not found.");
        return null;
      });
    targetLookupPromiseRef.current = request;
    return request;
  };

  const confirmResetAction = async (label) => {
    const sid = requireTargetId();
    if (!sid) return null;
    const normalizedSid = normalizeStudentId(sid);
    const identity =
      targetLookupStatus === "ready" && resolvedTarget?.studentId === normalizedSid
        ? resolvedTarget
        : await (targetLookupStatus === "loading" ? targetLookupPromiseRef.current : lookupTarget(normalizedSid));
    if (!identity || identity.studentId !== normalizedSid) return null;
    if (!window.confirm(getResetConfirmMessage(label, normalizedSid, identity))) return null;
    return normalizedSid;
  };

  useEffect(() => {
    setTargetStudentId(initialSid);
  }, [initialSid]);

  useEffect(() => {
    void lookupTarget(targetStudentId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetStudentId]);

  const fetchState = async (sidArg) => {
    const sid = String(sidArg || targetStudentId || "").trim().toUpperCase();
    if (!sid) {
      setError("Target Student ID is required.");
      return;
    }

    setLoading(true);
    setError("");
    const requestId = stateRequestRef.current + 1;
    stateRequestRef.current = requestId;
    try {
      const res = await fetch(
        `${API}/api/dev/student-reset/state?target_student_id=${encodeURIComponent(sid)}`,
        {
          cache: "no-store",
          headers: {
            "X-User": getAuthUserHeader(),
          },
        }
      );
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        throw new Error((data && (data.detail || data.error)) || "Failed to load dev state");
      }
      if (stateRequestRef.current !== requestId || targetIdRef.current !== sid) return;
      const next = data.state || null;
      setState(next);

      const cw = toInt(next?.current_week, 1);
      const cl = toInt(next?.current_lesson, 1);
    setTargetWeek(cw);
      setTargetLesson(cl);
    } catch (e) {
      if (stateRequestRef.current !== requestId || targetIdRef.current !== sid) return;
      setState(null);
      setError(e.message || "Failed to load dev state");
    } finally {
      if (stateRequestRef.current === requestId) setLoading(false);
    }
  };

  const fetchMentorState = async () => {
    const sid = requireTargetId(); if (!sid) return;
    const week = toInt(mentorWeek, 1);
    const res = await fetch(`${API}/api/dev/student-reset/mentor/state?target_student_id=${encodeURIComponent(sid)}&week=${week}`, { headers: { "X-User": getAuthUserHeader() } });
    const data = await res.json().catch(() => null);
    if (!res.ok) throw new Error(data?.detail || "Failed to load Mentor state");
    setMentorState(data.state || null);
  };

  const mentorAction = async (label, path, extra = {}) => {
    const sid = await confirmResetAction(label); if (!sid) return;
    await runAction(label, async () => {
      const data = await postJson(path, { target_student_id: sid, week: toInt(mentorWeek, 1), ...extra });
      setMentorState(data.state || null);
      return data;
    });
    await fetchMentorState();
  };

  useEffect(() => {
    fetchState(initialSid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialSid]);

  useEffect(() => {
    if (!initialSid) return;
    fetchState(initialSid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  useEffect(() => {
    setCertificate(null);
  }, [targetStudentId]);

  const runAction = async (label, fn, options = {}) => {
    const sid = String(targetStudentId || "").trim().toUpperCase();
    if (!sid) {
      setError("Target Student ID is required.");
      return;
    }
    setWorking(true);
    setMessage("");
    setError("");
    try {
      const data = await fn();
      const nextState = data?.state || null;
      setState(nextState);
      if (data?.llm_usage) {
        setMessage(
          `${label} completed. ${data.llm_usage.feature || "feedback_eval"} calls: ${data.llm_usage.previous_real_guard_count} -> ${data.llm_usage.new_real_guard_count}.`
        );
      } else {
        setMessage(`${label} completed.${nextState?.next_route ? ` Next route: ${nextState.next_route}` : ""}`);
      }
      if (
        label === "Full Course Reset" ||
        label === "Reset Current Week" ||
        label === "Reset Current Lesson" ||
        label === "Reset Lesson Attempts & Feedback" ||
        label === "Reset Lesson Feedback" ||
        label === "Reset Lesson + Reflection + Mentor Flow"
      ) {
        const resetWeek = toInt(options?.navigateWeek ?? nextState?.current_week ?? targetWeek, null);
        const resetLesson = toInt(options?.navigateLesson ?? nextState?.current_lesson ?? targetLesson, null);
        clearDemoOnboardingCompletion(sid);
        if (label === "Full Course Reset" || label === "Reset Current Week") {
          clearStudentRuntimeCache(sid);
          clearStudentDraftCache(sid);
        }
        if (label === "Reset Current Lesson" || label === "Reset Lesson Attempts & Feedback" || label === "Reset Lesson Feedback" || label === "Reset Lesson + Reflection + Mentor Flow") {
          clearLessonScopedBrowserCache(sid, resetWeek, resetLesson);
          clearStudentFeedbackCache(sid, resetWeek, resetLesson);
        }
        try {
          window.dispatchEvent(
            new CustomEvent("writeflow:lesson-reset", {
              detail: {
                studentId: sid,
                week: resetWeek,
                lesson: resetLesson,
              },
            })
          );
        } catch {
          // non-blocking
        }
      }
      const resetWasForW4L4 =
        label === "Reset Current Lesson" &&
        toInt((data?.state || state)?.current_week, null) === 4 &&
        toInt((data?.state || state)?.current_lesson, null) === 4;
      if (resetWasForW4L4) {
        resetW4L4ChartIntroState();
      }

      const week = toInt(options?.navigateWeek ?? nextState?.current_week, null);
      const lesson = toInt(options?.navigateLesson ?? nextState?.current_lesson, null);
      if (week && lesson && typeof onNavigateLesson === "function") {
        onNavigateLesson(week, lesson, { devJump: Boolean(options?.devJump) });
      }

      try {
        window.dispatchEvent(new CustomEvent("studentXpUpdated", { detail: { studentId: sid } }));
      } catch {
        // non-blocking
      }
      try {
        window.dispatchEvent(
          new CustomEvent("studentProgressRefetch", {
            detail: {
              studentId: sid,
              currentWeek: nextState?.current_week ?? targetWeek,
              currentLesson: nextState?.current_lesson ?? targetLesson,
            },
          })
        );
      } catch {
        // non-blocking
      }
      await fetchState(sid);
    } catch (e) {
      setError(e.message || `${label} failed.`);
    } finally {
      setWorking(false);
    }
  };

  const generateTestCertificate = async () => {
    const sid = await confirmResetAction("Generate Test Certificate");
    if (!sid) return;

    setCertificateWorking(true);
    setMessage("");
    setError("");
    const downloadToken = `${Date.now()}`;
    try {
      const res = await fetch(`${API}/api/admin/students/${encodeURIComponent(sid)}/certificate/generate-test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify({
          verification_origin: getVerificationOrigin(),
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || data?.ok === false) {
        throw new Error((data && (data.detail || data.error)) || "Failed to generate test certificate.");
      }
      setCertificate({
        ...data,
        download_url: addDownloadCacheBuster(data.download_url, downloadToken),
      });
      setMessage(`Generate Test Certificate completed for ${sid}.`);
      await fetchState(sid);
    } catch (e) {
      setError(e.message || "Failed to generate test certificate.");
    } finally {
      setCertificateWorking(false);
    }
  };

  const sid = String(targetStudentId || "").trim().toUpperCase();

  return (
    <div className="dev-reset-panel">
      <div className="dev-reset-title">DEV Reset Tools</div>
      <div className="dev-reset-helper">Reset actions below apply only to Target ID.</div>

      <label className="dev-reset-label" htmlFor="dev-reset-student-id">
        Target ID
      </label>
      <input
        id="dev-reset-student-id"
        className="dev-reset-input"
        value={targetStudentId}
        onChange={(e) => setTargetStudentId(e.target.value.toUpperCase())}
        placeholder="SM01K4R9"
      />

      <button
        type="button"
        className="sp-link"
        disabled={working || loading || !sid}
        onClick={() => fetchState(sid)}
      >
        Refresh State
      </button>

      <div className="dev-reset-state">
        {loading ? (
          <div>Loading state...</div>
        ) : state ? (
          <>
            <div>Current: W{state.current_week} L{state.current_lesson}</div>
            <div>Attempts (current lesson): {state.current_attempt_count}</div>
            <div>
              LLM calls ({state.llm_usage?.guard_feature || "feedback_eval"}):{" "}
              {state.llm_usage?.guard_count ?? 0}
            </div>
            {state.llm_usage?.legacy_unknown_guard_count ? (
              <div>Legacy unknown LLM calls: {state.llm_usage.legacy_unknown_guard_count}</div>
            ) : null}
            <div>
              Lesson closed: {state.current_lesson_state?.closed ? "yes" : "no"} / Needs work:{" "}
              {state.current_lesson_state?.needs_work ? "yes" : "no"}
            </div>
            <div>
              Week intro completed (current week): {state.week_intro?.intro_completed ? "yes" : "no"}
            </div>
            <div>
              Pending reflection week: {state.pending_reflection_week == null ? "none" : state.pending_reflection_week}
            </div>
            <div>
              Prior week reflection exists: {state.prior_week_reflection?.exists ? "yes" : "no"}
            </div>
          </>
        ) : (
          <div>No state loaded.</div>
        )}
      </div>

      <section className="dev-reset-mentor-controls">
        <h3>Mentor Flow Test Controls</h3>
        <p className="dev-reset-helper">Mentor-only test actions preserve lessons, submissions, reflections, and canonical reviews.</p>
        <label className="dev-reset-label" htmlFor="dev-mentor-week">Mentor Week</label>
        <select id="dev-mentor-week" className="dev-reset-input" value={mentorWeek} onChange={(e) => setMentorWeek(e.target.value)}>
          {[1, 2, 3, 4].map((week) => <option key={week} value={week}>{week}</option>)}
        </select>
        <button type="button" className="sp-link" disabled={working} onClick={fetchMentorState}>Refresh Mentor State</button>
        {mentorState ? <div className="dev-reset-state">
          <div>Lesson readiness: {mentorState.reflection_complete ? "ready" : "not ready"}</div>
          <div>Reflection readiness: {mentorState.reflection_complete ? "ready" : "not ready"}</div>
          <div>Canonical review: {mentorState.review_id ? `${mentorState.review_id}/v${mentorState.review_version} (${mentorState.status})` : "none"}</div>
          <div>Acknowledged: {mentorState.acknowledged ? "yes" : "no"}</div>
          <div>Next destination: {mentorState.next_destination || "—"}</div>
        </div> : null}
        <div className="dev-reset-grid">
          <button type="button" className="sp-link" disabled={working} onClick={() => {
            if (sid === "SMADMIN" && !window.confirm("Preview the SMADMIN Mentor page as the authenticated admin?")) return;
            window.open(`/student/${sid}/week-mentor/${toInt(mentorWeek, 1)}?preview=admin`, "_blank");
          }}>Preview Mentor Page as Admin</button>
          {sid && resolvedTarget?.studentId === sid && getAuthUserHeader().toUpperCase() !== sid ? <div className="dev-reset-helper">Warning: authenticated account {getAuthUserHeader()} differs from viewed student {resolvedTarget.name || sid} ({sid}).</div> : null}
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Reset Mentor Acknowledgement", "/api/dev/student-reset/mentor/reset-acknowledgement")}>Reset Mentor Acknowledgement</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Reopen Mentor Step", "/api/dev/student-reset/mentor/reopen")}>Reopen Mentor Step</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Reuse Ready Mentor Review", "/api/dev/student-reset/mentor/seed-ready")}>Reuse Ready Review</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Rebuild Deterministic Mentor Review", "/api/dev/student-reset/mentor/seed-ready", { rebuild: true })}>Rebuild Deterministic Review</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Simulate Mentor Not Ready", "/api/dev/student-reset/mentor/not-ready")}>Simulate Not Ready</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Simulate Mentor Acknowledgement Failure", "/api/dev/student-reset/mentor/ack-failure")}>Simulate Ack Failure</button>
          <button type="button" className="sp-link" disabled={working} onClick={() => mentorAction("Restore Mentor Normal State", "/api/dev/student-reset/mentor/restore")}>Restore Normal State</button>
        </div>
      </section>

      <div className="dev-reset-grid">
        <button
          type="button"
          className="sp-link sp-link-danger"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Full Course Reset");
            if (!confirmedSid) return;
            runAction("Full Course Reset", () =>
              postJson("/api/dev/student-reset/full", { target_student_id: confirmedSid })
            );
          }}
        >
          Full Course Reset
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Current Week");
            if (!confirmedSid) return;
            runAction("Reset Current Week", () =>
              postJson("/api/dev/student-reset/current-week", { target_student_id: confirmedSid })
            );
          }}
        >
          Reset Current Week
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Current Lesson");
            if (!confirmedSid) return;
            runAction("Reset Current Lesson", () =>
              postJson("/api/dev/student-reset/current-lesson", { target_student_id: confirmedSid })
            );
          }}
        >
          Reset Current Lesson
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Lesson Attempts & Feedback");
            if (!confirmedSid) return;
            runAction("Reset Lesson Attempts & Feedback", () =>
              postJson("/api/dev/student-reset/lesson-attempts", {
                target_student_id: confirmedSid,
                week: toInt(targetWeek, 1),
                lesson: toInt(targetLesson, 1),
              })
            );
          }}
        >
          Reset Lesson Attempts & Feedback
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Lesson Feedback");
            if (!confirmedSid) return;
            runAction("Reset Lesson Feedback", () =>
              postJson("/api/dev/student-reset/lesson-feedback", {
                target_student_id: confirmedSid,
                week: toInt(targetWeek, 1),
                lesson: toInt(targetLesson, 1),
              })
            );
          }}
        >
          Reset Lesson Feedback Only
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Student LLM Calls");
            if (!confirmedSid) return;
            runAction("Reset Student LLM Calls", () =>
              postJson("/api/dev/student-reset/llm-usage", { target_student_id: confirmedSid })
            );
          }}
        >
          Reset Student LLM Calls: {state?.llm_usage?.guard_count ?? 0}
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Week Intro");
            if (!confirmedSid) return;
            runAction("Reset Week Intro", () =>
              postJson("/api/dev/student-reset/week-intro", {
                target_student_id: confirmedSid,
                week: toInt(targetWeek, 1),
              })
            );
          }}
        >
          Reset Week Intro Only
        </button>

        <button
          type="button"
          className="sp-link"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Weekly Reflection");
            if (!confirmedSid) return;
            runAction("Reset Weekly Reflection", () =>
              postJson("/api/dev/student-reset/weekly-reflection", {
                target_student_id: confirmedSid,
                week: toInt(targetWeek, 1),
              })
            );
          }}
        >
          Reset Weekly Reflection Only
        </button>

        <button
          type="button"
          className="sp-link sp-link-danger"
          disabled={working}
          onClick={async () => {
            const confirmedSid = await confirmResetAction("Reset Lesson + Reflection + Mentor Flow");
            if (!confirmedSid) return;
            runAction("Reset Lesson + Reflection + Mentor Flow", () =>
              postJson("/api/dev/student-reset/lesson-reflection-mentor-flow", {
                target_student_id: confirmedSid,
                week: toInt(targetWeek, 1),
                lesson: toInt(targetLesson, 1),
              }), { navigateWeek: toInt(targetWeek, 1), navigateLesson: toInt(targetLesson, 1) }
            );
          }}
        >
          Reset Lesson + Reflection + Mentor Flow
        </button>
      </div>

      <div className="dev-reset-grid">
        <button
          type="button"
          className="sp-link"
          disabled={working || certificateWorking}
          onClick={generateTestCertificate}
        >
          {certificateWorking ? "Generating..." : "Generate Test Certificate"}
        </button>
        {certificate ? (
          <div className="dev-reset-cert">
            <div>Certificate: {certificate.certificate_id}</div>
            <a href={`${API}/api/certificates/verify/${encodeURIComponent(certificate.certificate_id)}`} target="_blank" rel="noreferrer">
              View JSON
            </a>
            <a href={`${API}${certificate.download_url}`} target="_blank" rel="noreferrer">
              Download Certificate
            </a>
            <a href={certificate.verification_url} target="_blank" rel="noreferrer">
              Open Verification Page
            </a>
          </div>
        ) : null}
      </div>

      <div className="dev-reset-jump-row">
        <label className="dev-reset-label">Week</label>
        <input
          className="dev-reset-input"
          type="number"
          min="1"
          value={targetWeek}
          onChange={(e) => setTargetWeek(e.target.value)}
        />
        <label className="dev-reset-label">Lesson</label>
        <input
          className="dev-reset-input"
          type="number"
          min="1"
          value={targetLesson}
          onChange={(e) => setTargetLesson(e.target.value)}
        />
      </div>

      <div className="dev-reset-jump-row">
        <label className="dev-reset-label">Mode</label>
        <input className="dev-reset-input" value="raw_jump" readOnly />

        <button
          type="button"
          className="sp-link"
          disabled={working || !sid}
          onClick={() =>
            runAction(
              "Set Student Position",
              () =>
                postJson("/api/dev/student-reset/set-position", {
                  target_student_id: sid,
                  week: toInt(targetWeek, 1),
                  lesson: toInt(targetLesson, 1),
                  mode: "raw_jump",
                }),
              {
                devJump: true,
                navigateWeek: toInt(targetWeek, 1),
                navigateLesson: toInt(targetLesson, 1),
              }
            )
          }
        >
          Set Current Position
        </button>
      </div>

      {message ? <div className="dev-reset-ok">{message}</div> : null}
      {error ? <div className="dev-reset-error">{error}</div> : null}
    </div>
  );
}
